import { useState, useEffect } from 'react';
import { adminGet } from '../lib/api';

const STAGES = [
  'Document Intake', 'Format Detection', 'Text Extraction', 'Structure Analysis',
  'Clause Segmentation', 'Posture Assignment', 'Jurisdiction Mapping',
  'Clause Risk Assessment', 'Compliance Cross-Reference', 'Severity Scoring',
  'Recommendation Generation', 'Dossier Assembly', 'Attestation Anchoring', 'Quality Verification',
];

interface PipelineJob {
  id: string;
  posture: string;
  jurisdiction: string;
  status: string;
  currentStage: number;
  totalStages: number;
  createdAt: number;
  completedAt: number | null;
  durationMs: number | null;
  demo: boolean;
}

const MOCK_JOBS: PipelineJob[] = [
  { id: 'p-001', posture: 'Athlete', jurisdiction: 'CA', status: 'complete', currentStage: 14, totalStages: 14, createdAt: Date.now() - 60000, completedAt: Date.now(), durationMs: 42000, demo: false },
  { id: 'p-002', posture: 'Athlete', jurisdiction: 'TX', status: 'processing', currentStage: 8, totalStages: 14, createdAt: Date.now() - 30000, completedAt: null, durationMs: null, demo: false },
  { id: 'p-003', posture: 'Sponsor', jurisdiction: 'FL', status: 'complete', currentStage: 14, totalStages: 14, createdAt: Date.now() - 120000, completedAt: Date.now() - 80000, durationMs: 38000, demo: true },
];

export function PipelineMonitor() {
  const [jobs, setJobs] = useState<PipelineJob[]>(MOCK_JOBS);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDemo, setShowDemo] = useState(false);

  useEffect(() => {
    (async () => {
      const params = new URLSearchParams({ status: statusFilter, limit: '50' });
      if (showDemo) params.set('demo', 'true');
      const resp = await adminGet(`/api/admin/analytics/pipelines?${params}`);
      if (resp.ok && resp.data && (resp.data as { jobs: PipelineJob[] }).jobs?.length > 0) {
        setJobs((resp.data as { jobs: PipelineJob[] }).jobs);
      }
    })();
  }, [statusFilter, showDemo]);

  function formatDuration(ms: number | null) {
    if (!ms) return '—';
    return `${Math.round(ms / 1000)}s`;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-display font-bold text-white">Pipeline Monitor</h1>
        <div className="flex gap-2">
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-1.5 text-sm bg-dark-700 text-dark-300 rounded-lg border border-dark-650 focus:border-rights-500 focus:outline-none">
            <option value="all">All Status</option>
            <option value="processing">Processing</option>
            <option value="complete">Complete</option>
            <option value="error">Errors</option>
          </select>
          <button onClick={() => setShowDemo(!showDemo)}
            className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${showDemo ? 'bg-severity-medium/10 text-severity-medium border-severity-medium/20' : 'bg-dark-700 text-dark-400 border-dark-650'}`}>
            {showDemo ? 'Demo Shown' : 'Hide Demo'}
          </button>
          <button onClick={() => window.location.reload()}
            className="px-3 py-1.5 text-sm bg-rights-500/10 text-rights-500 rounded-lg border border-rights-500/20 hover:bg-rights-500/20 transition-colors">
            Refresh
          </button>
        </div>
      </div>

      {/* Stage Legend */}
      <div className="bg-dark-700 rounded-2xl border border-dark-650/50 p-4 mb-6 overflow-x-auto">
        <div className="flex gap-1 min-w-[800px]">
          {STAGES.map((s, i) => (
            <div key={i} className="flex-1 text-center">
              <div className="text-[9px] text-dark-500 mb-1">S{i + 1}</div>
              <div className="text-[9px] text-dark-400 truncate px-0.5" title={s}>{s.split(' ')[0]}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Job Table */}
      <div className="bg-dark-700 rounded-2xl border border-dark-650/50 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-dark-650/50">
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase tracking-wider">Pipeline ID</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase tracking-wider">Posture</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase tracking-wider">Stage</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase tracking-wider">Duration</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase tracking-wider">State</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase tracking-wider"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-dark-650/30">
            {jobs.map(job => (
              <tr key={job.id} className="hover:bg-dark-750/50 transition-colors">
                <td className="px-4 py-3 text-sm text-dark-300 font-mono">{job.id.substring(0, 16)}...</td>
                <td className="px-4 py-3">
                  <span className="px-2 py-0.5 text-xs bg-rights-500/10 text-rights-500 rounded">{job.posture}</span>
                </td>
                <td className="px-4 py-3 text-sm font-mono text-dark-400">{job.currentStage}/{job.totalStages}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 text-xs rounded ${
                    job.status === 'complete' ? 'bg-severity-low/10 text-severity-low' :
                    job.status === 'processing' ? 'bg-rights-500/10 text-rights-500' :
                    'bg-severity-high/10 text-severity-high'
                  }`}>{job.status}</span>
                </td>
                <td className="px-4 py-3 text-sm font-mono text-dark-400">{formatDuration(job.durationMs)}</td>
                <td className="px-4 py-3">
                  <span className="px-2 py-0.5 text-xs bg-dark-800 text-dark-400 rounded">{job.jurisdiction}</span>
                </td>
                <td className="px-4 py-3">
                  {job.demo && <span className="px-2 py-0.5 text-[10px] bg-severity-medium/10 text-severity-medium rounded-full">DEMO</span>}
                </td>
              </tr>
            ))}
            {jobs.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-dark-500">No pipeline jobs found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
